import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MetricCard from "../components/MetricCard";
import AssetsTable from "../components/AssetsTable";
import YieldTimelineChart from "../components/YieldTimelineChart";
import SectorDonut from "../components/SectorDonut";
import DistributionsLedger from "../components/DistributionsLedger";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import WithdrawModal from "../components/WithdrawModal";
import ReinvestModal from "../components/ReinvestModal";
import { Button } from "../components/ui/button";
import { fetchPortfolio, fetchProperties } from "../lib/api";
import { getUser, saveUser } from "../lib/auth";
import { gbp } from "../lib/format";
import { Wallet, PoundSterling, Coins, TrendingUp, ArrowDownToLine, Sparkles } from "lucide-react";

export default function Portfolio() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [openWithdraw, setOpenWithdraw] = useState(false);
  const [openReinvest, setOpenReinvest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSector, setActiveSector] = useState(null);
  const [subTab, setSubTab] = useState("assets");

  const load = useCallback(async () => {
    const u = getUser();
    if (!u) {
      navigate("/", { replace: true });
      return;
    }
    setLoading(true);
    const [p, props] = await Promise.all([fetchPortfolio(u.id), fetchProperties()]);
    setData(p);
    setProperties(props);
    saveUser({ ...u, wallet_gbp: p.metrics.wallet_gbp });
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 bg-slate-200" />
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {[0,1,2,3].map((i) => <div key={i} className="h-32 rounded-lg bg-white border border-slate-200" />)}
            </div>
            <div className="h-64 rounded-lg bg-white border border-slate-200" />
          </div>
        </div>
      </div>
    );
  }

  const m = data.metrics;
  const hasUnclaimed = m.unclaimed_yield_gbp > 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portfolio-page">
      <Header />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <div className="overline">Portfolio</div>
              <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight text-slate-900">Your Holdings</h1>
              <p className="mt-2 text-sm text-slate-500">
                Signed in as <span className="font-mono-num text-slate-800">{data.user.email}</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => setOpenWithdraw(true)}
                className="border-slate-300 text-slate-800 hover:bg-slate-50"
                data-testid="withdraw-open-btn"
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" /> Withdraw to Bank
              </Button>
              <Button
                onClick={() => setOpenReinvest(true)}
                disabled={!hasUnclaimed}
                className="text-slate-900 hover:brightness-95 disabled:opacity-50"
                style={{ background: "#D4AF37" }}
                data-testid="reinvest-open-btn"
              >
                <Sparkles className="mr-2 h-4 w-4" /> Reinvest Yield
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="metric-cards">
          <MetricCard
            label="Total Portfolio Value"
            value={gbp(m.total_portfolio_value_gbp)}
            hint={`${data.investments.length} active ${data.investments.length === 1 ? "position" : "positions"}`}
            icon={PoundSterling}
            testId="metric-portfolio-value"
          />
          <MetricCard
            label="Active Tokens Owned"
            value={Math.round(m.active_tokens).toLocaleString()}
            hint="£1 per token"
            icon={Coins}
            testId="metric-active-tokens"
          />
          <MetricCard
            label="Total Yield Earned"
            value={gbp(m.total_yield_earned_gbp, { decimals: 2 })}
            hint={`${gbp(m.unclaimed_yield_gbp, { decimals: 2 })} unclaimed`}
            icon={TrendingUp}
            accent
            testId="metric-total-yield"
          />
          <MetricCard
            label="Available Cash Wallet"
            value={gbp(m.wallet_gbp)}
            hint="Instant deployment"
            icon={Wallet}
            testId="metric-wallet-balance"
          />
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <YieldTimelineChart investments={data.investments} />
          </div>
          <div className="lg:col-span-2">
            <SectorDonut
              investments={data.investments}
              activeSector={activeSector}
              onSectorClick={(label) => {
                setActiveSector(label);
                if (label) setSubTab("assets");
              }}
            />
          </div>
        </div>

        <div className="mt-10">
          <Tabs value={subTab} onValueChange={setSubTab}>
            <TabsList className="grid w-full max-w-md grid-cols-2 bg-slate-100/70 p-1" data-testid="portfolio-subtabs">
              <TabsTrigger value="assets" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="portfolio-tab-assets">
                My Assets
              </TabsTrigger>
              <TabsTrigger value="ledger" className="data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="portfolio-tab-ledger">
                Distributions Ledger
              </TabsTrigger>
            </TabsList>
            <TabsContent value="assets" className="mt-6" data-testid="portfolio-assets-content">
              <AssetsTable
                investments={data.investments}
                filterSector={activeSector}
                onClearFilter={() => setActiveSector(null)}
              />
            </TabsContent>
            <TabsContent value="ledger" className="mt-6" data-testid="portfolio-ledger-content">
              <DistributionsLedger investments={data.investments} userEmail={data.user.email} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <WithdrawModal
        open={openWithdraw}
        onOpenChange={setOpenWithdraw}
        wallet={m.wallet_gbp}
        onSuccess={load}
      />
      <ReinvestModal
        open={openReinvest}
        onOpenChange={setOpenReinvest}
        properties={properties}
        unclaimedYield={m.unclaimed_yield_gbp}
        onSuccess={load}
      />

      <Footer />
    </div>
  );
}
